import React, { useState } from 'react';
import { Card, CardContent, Stack, Typography, IconButton, Box, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Theme } from '@mui/material';
import { MoreVert, ArrowForward } from '@mui/icons-material';
import { ProgressItem } from './types';

export function ProgressCard({ good, improve }: { good: ProgressItem[]; improve: ProgressItem[] }) {
  const [openItem, setOpenItem] = useState<ProgressItem | null>(null);
  const [improvementSelections, setImprovementSelections] = useState<Record<string, string[]>>({});

  const handleToggleOption = (itemId: string, option: string) => {
    setImprovementSelections(prev => {
      const current = prev[itemId] || [];
      const exists = current.includes(option);
      return { ...prev, [itemId]: exists ? current.filter(o => o !== option) : [...current, option] };
    });
  };

  return (
    <>
      <Card variant="outlined"
        sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 1 }}>
            <Typography component="h6"
              variant="textLG"
              fontWeight={800}>Your Progress</Typography>
            <IconButton size="small"><MoreVert fontSize="small" /></IconButton>
          </Stack>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mb: 6 }}>AI-powered magic. What should you focus on this week?</Typography>
          <Divider sx={{ mb: 6 }} />
          <Stack spacing={6}>
            <Section title="What's gone well?"
              items={good}
              color="success"
              onItemClick={setOpenItem} />
            <Section title="What can you improve?"
              items={improve}
              color="warning"
              onItemClick={setOpenItem} />
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={!!openItem}
        onClose={() => setOpenItem(null)}
        maxWidth="sm"
        fullWidth>
        {openItem && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>{openItem.text}</DialogTitle>
            <DialogContent dividers>
              {openItem.details && (
                <Typography variant="textMD"
                  sx={{ whiteSpace: 'pre-line', mb: 4 }}>{openItem.details}</Typography>
              )}
              {openItem.improvementOptions && (
                <Stack spacing={2}>
                  <Typography variant="textMD"
                    color="text.secondary">Select one or more focused actions to drive improvement:</Typography>
                  {openItem.improvementOptions.map(opt => {
                    const selected = (improvementSelections[openItem.id] || []).includes(opt);
                    return (
                      <FormControlLabel key={opt}
                        control={<Checkbox size="small"
                          checked={selected}
                          onChange={() => handleToggleOption(openItem.id, opt)} />}
                        label={<Typography variant="textMD"
                          sx={{ fontWeight: selected ? 600 : 400 }}>{opt}</Typography>} />
                    );
                  })}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              {openItem.improvementOptions && (
                <Button
                  onClick={() => setOpenItem(null)}
                  disabled={(improvementSelections[openItem.id] || []).length === 0}>Apply</Button>
              )}
              <Button
                onClick={() => setOpenItem(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}

function Section({ title, items, color, onItemClick }: { title: string; items: ProgressItem[]; color: 'success' | 'warning'; onItemClick: (item: ProgressItem) => void }) {
  return (
    <Stack spacing={3}>
      <Typography variant="textMD"
        sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
      <Stack spacing={2}
        alignItems="flex-start">
        {items.map(i => (
          <ProgressPill
            key={i.id}
            color={color}
            leading={i.leading}
            text={i.text}
            onClick={() => onItemClick(i)}
          />
        ))}
      </Stack>
    </Stack>
  );
}

function ProgressPill({ leading, text, color, onClick }: { leading?: string; text: string; color: 'success' | 'warning'; onClick?: () => void }) {
  return (
    <Box onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        lineHeight: 1.2,
        bgcolor: (theme.palette[color].dark) + '66',
        border: 1,
        borderColor: (theme.palette[color].dark) + '66',
        px: 1.5,
        py: 1.5,
        borderRadius: 5,
        fontSize: 13,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color .15s, box-shadow .15s',
        '&:hover': {
          bgcolor: theme.palette[color].dark + '77',
        },
        outline: 'none',
        '&:focus-visible': { boxShadow: `0 0 0 2px ${theme.palette[color].main}AA` },
      })}
    >
      {leading && (
        <Box sx={(theme: Theme) => ({ backgroundColor: theme.palette.common.white, borderRadius: '12px', height: 24, m: '2px', px: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' })}>
          <Typography component="span"
            fontSize="14px">{leading}</Typography>
        </Box>
      )}
      <Typography component="span"
        fontSize="14px"
        sx={{ opacity: leading ? 0.85 : 0.9 }}>{text}</Typography>
      <ArrowForward sx={{ fontSize: 18, ml: 'auto', opacity: 0.6 }} />
    </Box>
  );
}
